export { escapeHtml } from './escape-html';

const structuralBreak = '\uE000';
const blockTags = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'PRE',
  'TR',
]);
const ignoredTags = new Set(['SCRIPT', 'STYLE', 'TEMPLATE']);
const firstListIndex = 1;
const listIndent = '  ';

type ListContext = { ordered: boolean; index: number };

const getStructuralSuffix = (tag: string): string => {
  if (blockTags.has(tag) || ['UL', 'OL', 'LI'].includes(tag))
    return structuralBreak;
  return tag === 'TD' || tag === 'TH' ? '\t' : '';
};

/**
 * Converts stored editor HTML to plain text in the browser. Parsing once decodes
 * entities without interpreting encoded literal tags as markup. Template content
 * stays inert; only text, formula source and structural separators are returned.
 */
export const richTextToPlainText = (
  value: string | null | undefined,
): string => {
  if (!value) return '';
  const template = document.createElement('template');
  template.innerHTML = value;
  const lists: ListContext[] = [];

  const read = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
    if (!(node instanceof Element)) return '';
    if (ignoredTags.has(node.tagName)) return '';
    const latex = node.getAttribute('data-latex');
    if (latex !== null)
      return latex ? `$$${latex}$$${getStructuralSuffix(node.tagName)}` : '';
    if (node.tagName === 'BR') return '\n';
    if (node.tagName === 'IMG') return node.getAttribute('alt') ?? '';

    const isList = node.tagName === 'UL' || node.tagName === 'OL';
    if (isList) {
      const start = Number.parseInt(node.getAttribute('start') ?? '', 10);
      lists.push({
        ordered: node.tagName === 'OL',
        index: Number.isNaN(start) ? firstListIndex : start,
      });
    }
    let prefix = '';
    if (node.tagName === 'LI') {
      const list = lists.at(-1);
      const marker = list?.ordered ? `${String(list.index++)}. ` : '- ';
      prefix =
        structuralBreak +
        listIndent.repeat(Math.max(0, lists.length - 1)) +
        marker;
    }
    const content = Array.from(node.childNodes, read).join('');
    if (isList) lists.pop();
    // Generated list markers must not turn an empty editor into content.
    if (!content.replaceAll(structuralBreak, '').trim()) prefix = '';
    return prefix + content + getStructuralSuffix(node.tagName);
  };

  return Array.from(template.content.childNodes, read)
    .join('')
    .replace(/\u00a0/g, ' ')
    .replace(/\uE000+/g, structuralBreak)
    .replace(/\n?\uE000\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};
